/**
 * Onboarding Store
 *
 * Zustand store for onboarding progress management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  OnboardingProgress,
  DogProfileInput,
  HealthInfoInput,
  PreferencesInput,
} from '@/shared/types/onboarding.types';

interface OnboardingStore {
  progress: OnboardingProgress;
  setStep: (step: 1 | 2 | 3) => void;
  addDog: (dog: DogProfileInput) => void;
  removeDog: (index: number) => void;
  updateDog: (index: number, dog: DogProfileInput) => void;
  setHealthInfo: (healthInfo: HealthInfoInput) => void;
  setPreferences: (preferences: PreferencesInput) => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set) => ({
      progress: {
        step: 1,
        dogs: [],
      },
      setStep: (step) =>
        set((state) => ({
          progress: { ...state.progress, step },
        })),
      addDog: (dog) =>
        set((state) => ({
          progress: {
            ...state.progress,
            dogs: [...state.progress.dogs, dog],
          },
        })),
      removeDog: (index) =>
        set((state) => ({
          progress: {
            ...state.progress,
            dogs: state.progress.dogs.filter((_, i) => i !== index),
          },
        })),
      updateDog: (index, dog) =>
        set((state) => ({
          progress: {
            ...state.progress,
            dogs: state.progress.dogs.map((d, i) => (i === index ? dog : d)),
          },
        })),
      setHealthInfo: (healthInfo) =>
        set((state) => ({
          progress: { ...state.progress, healthInfo },
        })),
      setPreferences: (preferences) =>
        set((state) => ({
          progress: { ...state.progress, preferences },
        })),
      completeOnboarding: () =>
        set((state) => ({
          progress: {
            ...state.progress,
            completedAt: new Date().toISOString(),
          },
        })),
      resetOnboarding: () =>
        set({
          progress: {
            step: 1,
            dogs: [],
          },
        }),
    }),
    {
      name: 'onboarding-storage',
    }
  )
);
